import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";
import Backstage from "./Backstage";

import { API_URL } from "../../config";

const _sanitizeFilenameCk = (s) => (s || "checkin").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 60);

const exportCheckinListExcel = (list, title) => {
  const rows = list.map((t, i) => ({
    "#": i + 1,
    "Bilhete": t.ticket_number || "",
    "Nome": t.name || "",
    "Data Sessão": t.session_date || "",
    "Hora": t.session_time || "",
    "Estado": t.checked_in ? "Validado" : "Pendente",
    "Check-in": t.checked_in_at ? new Date(t.checked_in_at).toLocaleString("pt-PT") : "",
  }));
  const header = [{
    "#": title || "Lista de Check-Ins",
    "Bilhete": `${list.length} bilhete(s)`,
    "Nome": "",
    "Data Sessão": "",
    "Hora": "",
    "Estado": "",
    "Check-in": `Exportado em ${new Date().toLocaleString("pt-PT")}`,
  }, {}];
  const ws = XLSX.utils.json_to_sheet([...header, ...rows], { skipHeader: false });
  ws["!cols"] = [{ wch: 5 }, { wch: 18 }, { wch: 30 }, { wch: 14 }, { wch: 8 }, { wch: 12 }, { wch: 22 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "CheckIns");
  XLSX.writeFile(wb, `checkins_${_sanitizeFilenameCk(title)}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

const exportCheckinListPDF = (list, title) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  doc.setFontSize(15); doc.text(title || "Lista de Check-Ins", 40, 40);
  doc.setFontSize(10); doc.setTextColor(80);
  doc.text(`Total: ${list.length} bilhete(s)`, 40, 60);
  doc.text(`Exportado em: ${new Date().toLocaleString("pt-PT")}`, 40, 74);
  const checked = list.filter((t) => t.checked_in).length;
  doc.text(`Validados: ${checked} · Pendentes: ${list.length - checked}`, 40, 88);
  autoTable(doc, {
    startY: 108,
    head: [["#", "Bilhete", "Nome", "Sessão", "Estado", "Check-in"]],
    body: list.map((t, i) => [
      String(i + 1),
      t.ticket_number || "",
      t.name || "",
      `${t.session_date || ""} ${t.session_time || ""}`.trim(),
      t.checked_in ? "Validado" : "Pendente",
      t.checked_in_at ? new Date(t.checked_in_at).toLocaleString("pt-PT") : "",
    ]),
    styles: { fontSize: 9, cellPadding: 5, valign: "top" },
    headStyles: { fillColor: [24, 24, 27], textColor: 255 },
    columnStyles: {
      0: { cellWidth: 24 },
      1: { cellWidth: 90 },
      2: { cellWidth: "auto" },
      3: { cellWidth: 90 },
      4: { cellWidth: 60 },
      5: { cellWidth: 110 },
    },
    theme: "grid",
  });
  doc.save(`checkins_${_sanitizeFilenameCk(title)}_${new Date().toISOString().slice(0, 10)}.pdf`);
};


export const TicketScanner = () => {
  const [scanResult, setScanResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [bsTotals, setBsTotals] = useState({ vips: 0, birthdays: 0 });
  const [manualTicket, setManualTicket] = useState("");
  const [scannerActive, setScannerActive] = useState(false);
  const scannerRef = useRef(null);
  const html5QrcodeScannerRef = useRef(null);
  const [ticketListModal, setTicketListModal] = useState(null);
  const [ticketList, setTicketList] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchBsTotals();
    const interval = setInterval(() => {
      fetchStats();
      fetchBsTotals();
    }, 30000);
    return () => {
      clearInterval(interval);
      if (html5QrcodeScannerRef.current) {
        html5QrcodeScannerRef.current.stop().then(() => {
          html5QrcodeScannerRef.current.clear();
        }).catch(console.error);
      }
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/checkin-stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchBsTotals = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/backstage-stats`, { withCredentials: true });
      const t = r.data?.totals || {};
      setBsTotals({ vips: t.total_vips_arrived || 0, birthdays: t.total_birthdays_today || 0 });
    } catch (e) { console.error("Backstage totals fetch failed:", e); }
  };

  const startScanner = () => {
    setScannerActive(true);
    setTimeout(() => {
      if (scannerRef.current && !html5QrcodeScannerRef.current) {
        const html5Qr = new Html5Qrcode("qr-reader");
        html5QrcodeScannerRef.current = html5Qr;
        html5Qr.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
            focusMode: "continuous",
            advanced: [
              { focusMode: "continuous" },
              { focusDistance: { ideal: 0.25 } },
            ],
          },
          onScanSuccess,
          onScanFailure
        ).catch((err) => {
          console.error("Camera error:", err);
          toast.error("Erro ao aceder à câmara. Verifique as permissões.");
          setScannerActive(false);
        });
      }
    }, 100);
  };

  const stopScanner = () => {
    if (html5QrcodeScannerRef.current) {
      html5QrcodeScannerRef.current.stop().then(() => {
        html5QrcodeScannerRef.current.clear();
        html5QrcodeScannerRef.current = null;
      }).catch(console.error);
    }
    setScannerActive(false);
  };

  const onScanSuccess = async (decodedText) => {
    setScanResult(decodedText);
    await validateTicket(decodedText);
  };

  const onScanFailure = (error) => {
    // Ignore scan failures (no QR code in view)
  };

  const validateTicket = async (ticketData) => {
    setLoading(true);
    setValidationResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/validate-ticket`,
        { ticket_number: ticketData },
        { withCredentials: true }
      );
      setValidationResult(response.data);
    } catch (error) {
      setValidationResult({
        valid: false,
        status: "error",
        message: error.response?.data?.detail || "Erro ao validar bilhete"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async () => {
    if (!scanResult && !manualTicket) return;
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/checkin-ticket`,
        { ticket_number: scanResult || manualTicket },
        { withCredentials: true }
      );
      toast.success("Check-in realizado com sucesso!");
      setValidationResult({
        valid: true,
        status: "checked_in",
        message: "Check-in realizado!",
        ticket: response.data.ticket,
        session: response.data.session
      });
      fetchStats();
      fetchBsTotals();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar check-in");
    } finally {
      setLoading(false);
    }
  };

  const handleManualValidation = async () => {
    if (!manualTicket.trim()) return;
    setScanResult(manualTicket.trim());
    await validateTicket(manualTicket.trim());
  };

  const resetScanner = () => {
    stopScanner();
    setScanResult(null);
    setValidationResult(null);
    setManualTicket("");
  };

  const openTicketList = async (filter, title, sessionId = null) => {
    try {
      let url = `${API_URL}/api/admin/checkin-tickets?filter=${filter}`;
      if (sessionId) url += `&session_id=${sessionId}`;
      const res = await axios.get(url, { withCredentials: true });
      setTicketList(res.data);
      setTicketListModal(title);
    } catch (err) {
      toast.error("Erro ao carregar bilhetes");
    }
  };

  const handleUndoCheckin = async (ticketNumber) => {
    try {
      await axios.post(`${API_URL}/api/admin/undo-checkin/${ticketNumber}`, {}, { withCredentials: true });
      toast.success("Check-in revertido");
      setTicketList(prev => prev.filter(t => t.ticket_number !== ticketNumber));
      fetchStats();
      fetchBsTotals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reverter");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ready":
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case "checked_in":
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case "already_checked_in":
        return <AlertTriangle className="w-16 h-16 text-yellow-500" />;
      case "not_paid":
        return <AlertTriangle className="w-16 h-16 text-yellow-500" />;
      default:
        return <XCircle className="w-16 h-16 text-red-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ready":
      case "checked_in":
        return "border-green-500 bg-green-500/10";
      case "already_checked_in":
      case "not_paid":
        return "border-yellow-500 bg-yellow-500/10";
      default:
        return "border-red-500 bg-red-500/10";
    }
  };

  return (
    <div data-testid="ticket-scanner">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestão de Check-Ins</h1>
          <p className="text-zinc-500 text-sm mt-1">Scanner QR + painel em tempo real</p>
        </div>
        <Button onClick={fetchStats} className="btn-secondary" data-testid="refresh-stats-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Unified Stats — clickable for drill-down */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div
            className="bg-zinc-900 rounded-xl p-4 border border-white/10 cursor-pointer hover:border-white/30 transition-colors"
            onClick={() => openTicketList("all", "Total Bilhetes")}
            data-testid="stat-total"
          >
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Vendidos</p>
            <p className="text-2xl font-bold text-blue-500 mt-1">{stats.total.tickets}</p>
            <p className="text-zinc-600 text-[10px] mt-1">Clique p/ ver</p>
          </div>
          <div
            className="bg-zinc-900 rounded-xl p-4 border border-white/10 cursor-pointer hover:border-green-500/30 transition-colors"
            onClick={() => openTicketList("checked_in", "Check-ins Realizados")}
            data-testid="stat-checkins"
          >
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Check-ins</p>
            <p className="text-2xl font-bold text-green-500 mt-1">{stats.total.checked_in}</p>
            <p className="text-zinc-600 text-[10px] mt-1">{stats.total.percentage}% · Clique p/ ver</p>
          </div>
          <div
            className="bg-zinc-900 rounded-xl p-4 border border-white/10 cursor-pointer hover:border-amber-500/30 transition-colors"
            onClick={() => openTicketList("pending", "Pendentes (Não Validados)")}
            data-testid="stat-pending"
          >
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Pendentes</p>
            <p className="text-2xl font-bold text-yellow-500 mt-1">{stats.total.pending}</p>
            <p className="text-zinc-600 text-[10px] mt-1">Clique p/ ver</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-white/10" data-testid="stat-vips">
            <p className="text-zinc-500 text-xs uppercase tracking-wider">⭐ VIPs</p>
            <p className="text-2xl font-bold text-amber-400 mt-1">{bsTotals.vips}</p>
            <p className="text-zinc-600 text-[10px] mt-1">chegaram</p>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-white/10" data-testid="stat-birthdays">
            <p className="text-zinc-500 text-xs uppercase tracking-wider">🎂 Aniversários</p>
            <p className="text-2xl font-bold text-pink-400 mt-1">{bsTotals.birthdays}</p>
            <p className="text-zinc-600 text-[10px] mt-1">hoje</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Scanner Area */}
        <div className="space-y-6">
          <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Camera className="w-5 h-5 text-amber-500" />
              Scanner QR Code
            </h3>
            
            {!scannerActive ? (
              <div className="text-center py-12">
                <QrCode className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500 mb-4">Clique para ativar a câmara</p>
                <Button onClick={startScanner} className="btn-primary" data-testid="start-scanner-btn">
                  <Camera className="w-4 h-4 mr-2" />
                  Ativar Scanner
                </Button>
              </div>
            ) : (
              <div>
                <div id="qr-reader" ref={scannerRef} className="rounded-lg overflow-hidden mb-4" />
                <div className="flex gap-2">
                  <Button onClick={stopScanner} variant="outline" className="flex-1 btn-secondary" data-testid="stop-scanner-btn">
                    <XCircle className="w-4 h-4 mr-2" />
                    Parar Câmara
                  </Button>
                  <Button onClick={resetScanner} variant="outline" className="flex-1 text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="reset-all-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reiniciar Tudo
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Manual Entry */}
          <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">Entrada Manual</h3>
            <div className="flex gap-2">
              <Input
                value={manualTicket}
                onChange={(e) => setManualTicket(e.target.value.toUpperCase())}
                placeholder="TKT-XXXXXXXX"
                className="input-dark font-mono"
                data-testid="manual-ticket-input"
              />
              <Button 
                onClick={handleManualValidation} 
                className="btn-primary"
                disabled={!manualTicket.trim() || loading}
                data-testid="validate-manual-btn"
              >
                Validar
              </Button>
            </div>
          </div>
        </div>

        {/* Result Area */}
        <div className="space-y-6">
          {validationResult ? (
            <div className={`bg-zinc-900 rounded-xl p-6 border-2 ${getStatusColor(validationResult.status)}`}>
              <div className="text-center mb-6">
                {getStatusIcon(validationResult.status)}
                <h3 className="text-xl font-bold text-white mt-4">{validationResult.message}</h3>
              </div>

              {validationResult.ticket && (
                <div className="space-y-3 border-t border-white/10 pt-4">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Bilhete</span>
                    <span className="text-amber-500 font-mono">{validationResult.ticket.ticket_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Nome</span>
                    <span className="text-white">{validationResult.ticket.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Igreja</span>
                    <span className="text-white">{validationResult.ticket.church}</span>
                  </div>
                  {validationResult.session && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Sessão</span>
                        <span className="text-white">{validationResult.session.date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Horário</span>
                        <span className="text-white">{validationResult.session.time}</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                {validationResult.status === "ready" && (
                  <Button 
                    onClick={handleCheckin} 
                    className="flex-1 btn-primary py-6 text-lg"
                    disabled={loading}
                    data-testid="confirm-checkin-btn"
                  >
                    {loading ? "A processar..." : "Confirmar Check-in"}
                  </Button>
                )}
                <Button 
                  onClick={resetScanner} 
                  variant="outline" 
                  className={`btn-secondary ${validationResult.status === "ready" ? "" : "flex-1"}`}
                  data-testid="reset-scanner-btn"
                >
                  Novo Scan
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10 text-center py-12">
              <QrCode className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-500">Escaneie um QR code ou digite o número do bilhete</p>
            </div>
          )}
        </div>
      </div>

      {/* Ticket List Modal */}
      {ticketListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setTicketListModal(null)}>
          <div className="bg-zinc-900 rounded-xl border border-white/10 w-full max-w-lg max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10 gap-2 flex-wrap">
              <h3 className="text-white font-semibold">{ticketListModal} ({ticketList.length})</h3>
              <div className="flex items-center gap-1">
                {ticketList.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => exportCheckinListExcel(ticketList, ticketListModal)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10"
                      title="Descarregar em Excel"
                      data-testid="checkin-export-excel"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => exportCheckinListPDF(ticketList, ticketListModal)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-red-500/30 text-red-200 hover:bg-red-500/10"
                      title="Descarregar em PDF"
                      data-testid="checkin-export-pdf"
                    >
                      <FileText className="w-3.5 h-3.5" /> PDF
                    </button>
                  </>
                )}
                <button onClick={() => setTicketListModal(null)} className="text-zinc-400 hover:text-white p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-4 space-y-2">
              {ticketList.length === 0 ? (
                <p className="text-zinc-500 text-center py-8">Nenhum bilhete encontrado</p>
              ) : (
                ticketList.map((t) => (
                  <div key={t.ticket_number} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div>
                      <p className="text-white text-sm font-medium">{t.name}</p>
                      <p className="text-zinc-500 text-xs font-mono">{t.ticket_number}</p>
                      <p className="text-zinc-600 text-xs">{t.session_date} {t.session_time}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.checked_in ? (
                        <>
                          <span className="text-green-500 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" /> Validado
                          </span>
                          <button
                            onClick={() => handleUndoCheckin(t.ticket_number)}
                            className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10"
                            title="Reverter check-in"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <span className="text-amber-500 text-xs flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Pendente
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fused Backstage · Real-Time Panel (compact — occupancy + recent + VIPs + birthdays) */}
      <div className="mt-8" data-testid="fused-backstage-section">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Painel em Tempo Real
        </h3>
        <Backstage compact />
      </div>
    </div>
  );
};

// Main Dashboard Layout
// Emails Page Component
